import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { supabase } from '../supabaseClient';

const { FiCalendar, FiMail, FiCheckCircle, FiArrowRight, FiUsers, FiGlobe, FiFileText, FiLoader, FiDownload, FiLink } = FiIcons;

function CallToAction({ userInfo, overallScore, pdfUrl, onGeneratePdf, isGeneratingPdf }) {
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [showPdfDialog, setShowPdfDialog] = useState(false);

  // Scoring Correction: Max score is 280 (8 areas * 35), not 400
  const maxTotalScore = 280;
  
  useEffect(() => {
    const script = document.createElement('script');
    script.src = "https://cdnjs.cloudflare.com/ajax/libs/iframe-resizer/4.3.7/iframeResizer.min.js";
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const getScoreMessage = () => {
    const percentage = (overallScore / maxTotalScore) * 100;
    
    if (percentage >= 85) {
      return {
        title: "Congratulations on Your Strong Performance!",
        message: "Your organization demonstrates exceptional capabilities. Let's discuss how to leverage these strengths for even greater success and explore advanced optimization strategies.",
        urgency: "high"
      };
    } else if (percentage >= 70) {
      return {
        title: "You're On the Right Track!",
        message: "Your organization has solid foundations with clear opportunities for growth. We can help you prioritize and accelerate improvements in key areas.",
        urgency: "medium"
      };
    } else if (percentage >= 55) {
      return {
        title: "Growth Opportunities",
        message: "Your assessment reveals important areas where focused development can drive substantial improvements. Let's create a strategic roadmap for organizational growth.",
        urgency: "medium"
      };
    } else {
      return {
        title: "Transform Your Organization's Potential",
        message: "Your assessment shows tremendous opportunity for organizational development. We specialize in helping companies like yours build strong foundations for sustainable growth.",
        urgency: "high"
      };
    }
  };

  const scoreMessage = getScoreMessage();

  const handleBookingClick = () => {
    window.open('https://wendworks.trafft.com/booking?t=s&uuid=794c9454-0a68-48cd-bc03-159492650249', '_blank');
  };

  const handleShareClick = () => {
    setShowShareDialog(true);
  };
  
  const handlePdfClick = () => {
    if (pdfUrl) {
      window.open(pdfUrl, '_blank');
    } else {
      setShowPdfDialog(true);
    }
  };

  const generateReportLinks = (isInternal) => {
    const baseUrl = window.location.origin;
    const reportLink = `${baseUrl}/#/report/${userInfo.orgCode}/${encodeURIComponent(userInfo.email)}`;
    
    if (isInternal) {
      const assessmentLink = `${baseUrl}/#/?orgCode=${userInfo.orgCode}`;
      return { reportLink, assessmentLink };
    } else {
      const assessmentLink = `${baseUrl}/#/`;
      return { reportLink, assessmentLink };
    }
  };

  const handleShareConfirm = async (isInternal) => {
    const { reportLink, assessmentLink } = generateReportLinks(isInternal);
    const recipientType = isInternal ? 'Internal Team Member' : 'External Contact';
    
    const subject = encodeURIComponent(`Assessment Report from ${userInfo.name} - ${new Date().toLocaleDateString()}`);
    const body = encodeURIComponent(
      `Hello,\n\n${userInfo.name} has shared their Wendworks Organizational Health Assessment results with you.\n\n` +
      `📊 View the Complete Report: ${reportLink}\n\n` +
      `${pdfUrl ? `📄 Download PDF: ${pdfUrl}\n\n` : ''}` +
      `${isInternal ? `🎯 Take the Assessment for Your Organization: ${assessmentLink}\nUse organization code: ${userInfo.orgCode}` : `🎯 Take the Assessment for Your Organization: ${assessmentLink}`}\n\n` +
      `This assessment provides valuable insights into organizational health across 8 key areas, including Vision & Strategy, Leadership & Culture, Operations, and more.\n\n` +
      `Best regards,\n${userInfo.name}`
    );

    try {
      await supabase.from('shared_reports').insert({
        org_code: userInfo.orgCode,
        user_email: userInfo.email,
        share_type: recipientType,
        report_link: reportLink,
        created_at: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error storing share record:', error);
    }

    window.open(`mailto:?subject=${subject}&body=${body}`);
    setShowShareDialog(false);
  };
  
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
        alert("Link copied to clipboard!");
    });
  };

  return (
    <>
      <motion.div 
        id="action-plan"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
        className="bg-gradient-to-br from-blue-700 to-indigo-800 rounded-2xl shadow-2xl p-8 text-white relative overflow-hidden"
      >
        <div className="absolute top-0 right-0 p-4 opacity-10">
          <SafeIcon icon={FiArrowRight} className="w-64 h-64" />
        </div>

        <div className="text-center mb-10 relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 tracking-tight">
            {scoreMessage.title}
          </h2>
          <p className="text-xl opacity-90 mb-8 max-w-3xl mx-auto leading-relaxed font-light">
            {scoreMessage.message}
          </p>
          {scoreMessage.urgency === 'high' && (
            <div className="inline-flex items-center px-5 py-2 bg-yellow-400 text-yellow-900 rounded-full font-bold shadow-lg mb-6">
              <SafeIcon icon={FiArrowRight} className="w-5 h-5 mr-2" /> High Impact Opportunity
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="text-center relative z-10 mb-12">
          <div className="flex flex-col md:flex-row gap-4 justify-center items-center">
            <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={handleBookingClick} className="w-full md:w-auto flex items-center justify-center px-8 py-4 bg-white text-blue-700 rounded-xl font-bold text-lg hover:bg-yellow-400 hover:text-blue-900 transition-all shadow-xl">
              <SafeIcon icon={FiCalendar} className="w-5 h-5 mr-3" /> Book Review
            </motion.button>
            <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={handleShareClick} className="w-full md:w-auto flex items-center justify-center px-8 py-4 bg-transparent border-2 border-white text-white rounded-xl font-bold text-lg hover:bg-white hover:text-blue-700 transition-all shadow-lg">
              <SafeIcon icon={FiMail} className="w-5 h-5 mr-3" /> Share Report
            </motion.button>
            <motion.button 
              whileHover={{ scale: 1.05 }} 
              whileTap={{ scale: 0.95 }} 
              onClick={handlePdfClick} 
              className={`w-full md:w-auto flex items-center justify-center px-8 py-4 rounded-xl font-bold text-lg transition-all shadow-lg ${pdfUrl ? 'bg-green-500 text-white hover:bg-green-600' : 'bg-blue-600/50 border-2 border-blue-400 text-blue-100 hover:bg-blue-600'}`}
            >
              <SafeIcon icon={pdfUrl ? FiDownload : FiFileText} className="w-5 h-5 mr-3" /> 
              {pdfUrl ? 'View PDF' : 'Create PDF'}
            </motion.button>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-10 relative z-10">
          <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-xl p-8 border border-white border-opacity-20 shadow-inner">
            <h3 className="text-2xl font-bold mb-6 text-white border-b border-white border-opacity-20 pb-4">What We Can Do For You</h3>
            <ul className="space-y-4">
              <li className="flex items-start"><SafeIcon icon={FiCheckCircle} className="w-6 h-6 mr-3 mt-0.5 flex-shrink-0 text-green-400" /><span className="text-lg">Customized organizational development strategy</span></li>
              <li className="flex items-start"><SafeIcon icon={FiCheckCircle} className="w-6 h-6 mr-3 mt-0.5 flex-shrink-0 text-green-400" /><span className="text-lg">Leadership coaching and development programs</span></li>
              <li className="flex items-start"><SafeIcon icon={FiCheckCircle} className="w-6 h-6 mr-3 mt-0.5 flex-shrink-0 text-green-400" /><span className="text-lg">Process optimization and automation</span></li>
              <li className="flex items-start"><SafeIcon icon={FiCheckCircle} className="w-6 h-6 mr-3 mt-0.5 flex-shrink-0 text-green-400" /><span className="text-lg">Culture transformation initiatives</span></li>
            </ul>
          </div>

          <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-xl p-8 border border-white border-opacity-20 shadow-inner">
            <h3 className="text-2xl font-bold mb-6 text-white border-b border-white border-opacity-20 pb-4">Your Next Steps</h3>
            <div className="space-y-6">
              <div className="flex items-center"><div className="w-10 h-10 bg-white text-blue-700 rounded-full flex items-center justify-center text-lg font-bold mr-4 shadow-lg shrink-0">1</div><span className="text-lg">Schedule a complimentary 30-minute discovery call</span></div>
              <div className="flex items-center"><div className="w-10 h-10 bg-white text-blue-700 rounded-full flex items-center justify-center text-lg font-bold mr-4 shadow-lg shrink-0">2</div><span className="text-lg">Discuss your assessment results in detail</span></div>
              <div className="flex items-center"><div className="w-10 h-10 bg-white text-blue-700 rounded-full flex items-center justify-center text-lg font-bold mr-4 shadow-lg shrink-0">3</div><span className="text-lg">Receive personalized recommendations</span></div>
            </div>
          </div>
        </div>

        <div className="mt-16 pt-10 border-t border-blue-400/50">
          <h3 className="text-2xl font-bold text-center mb-10 text-white tracking-wide uppercase">Trusted Since 2019</h3>
          <div className="bg-white rounded-xl overflow-hidden shadow-lg p-2">
            <iframe id="p-75a66e1e-e871-43ba-a2a9-72c7096803e8" src="https://www.bonjoro.com/pe/75a66e1e-e871-43ba-a2a9-72c7096803e8" style={{ border: 0, width: '100%', minHeight: '400px' }} title="Bonjoro Social Proof" onLoad={() => { if (window.iFrameResize) { window.iFrameResize({ warningTimeout: 30000, log: false, checkOrigin: false }, '#p-75a66e1e-e871-43ba-a2a9-72c7096803e8'); } }}></iframe>
          </div>
        </div>
      </motion.div>

      {/* Share Dialog */}
      <AnimatePresence>
        {showShareDialog && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }} className="bg-white rounded-2xl w-full max-w-md overflow-hidden relative shadow-2xl">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-xl font-bold text-gray-800 text-center">Share Your Report</h3>
                <p className="text-gray-600 text-center mt-2">Who are you sending this to?</p>
              </div>
              <div className="p-6 space-y-4">
                <button onClick={() => handleShareConfirm(true)} className="w-full flex items-center justify-center p-4 bg-blue-50 hover:bg-blue-100 rounded-lg border-2 border-blue-200 transition-colors group">
                  <SafeIcon icon={FiUsers} className="w-6 h-6 text-blue-600 mr-3" />
                  <div className="text-left">
                    <div className="font-semibold text-gray-800 group-hover:text-blue-700">Internal Team Member</div>
                    <div className="text-sm text-gray-600">Someone within your organization</div>
                  </div>
                </button>
                <button onClick={() => handleShareConfirm(false)} className="w-full flex items-center justify-center p-4 bg-purple-50 hover:bg-purple-100 rounded-lg border-2 border-purple-200 transition-colors group">
                  <SafeIcon icon={FiGlobe} className="w-6 h-6 text-purple-600 mr-3" />
                  <div className="text-left">
                    <div className="font-semibold text-gray-800 group-hover:text-purple-700">External Contact</div>
                    <div className="text-sm text-gray-600">Someone outside your organization</div>
                  </div>
                </button>
                <button onClick={() => setShowShareDialog(false)} className="w-full mt-4 px-4 py-2 text-gray-600 hover:text-gray-800 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">Cancel</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* PDF Dialog */}
      <AnimatePresence>
        {showPdfDialog && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }} className="bg-white rounded-2xl w-full max-w-md overflow-hidden relative shadow-2xl text-center">
              <div className="p-8">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={FiFileText} className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-2">Create PDF Report</h3>
                <p className="text-gray-600 mb-6">
                  We'll generate a downloadable PDF of your full assessment report and create a shareable public link.
                </p>
                
                {isGeneratingPdf ? (
                  <div className="flex flex-col items-center justify-center py-4">
                    <SafeIcon icon={FiLoader} className="w-10 h-10 text-blue-600 animate-spin mb-2" />
                    <span className="text-sm font-medium text-blue-600">Generating and Saving...</span>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <button 
                      onClick={() => {
                        onGeneratePdf();
                        setShowPdfDialog(false);
                      }}
                      className="w-full py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition-colors shadow-md"
                    >
                      Generate & Save PDF
                    </button>
                    <button 
                      onClick={() => setShowPdfDialog(false)}
                      className="w-full py-3 bg-gray-100 text-gray-600 rounded-xl font-medium hover:bg-gray-200 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export default CallToAction;